<template>
    <div style="text-align: right;min-height:115px;width: 50%">
        <p v-for="(obj, key) in priceClasses"
            class="resource-row"
            :key="'resource-row-'+key">
            <span style="font-weight: bold;">{{ obj.names.join(', ') }}</span>
            <span>{{ obj.quantity * (activeReservation.recurringDates.length + 1) }}
            {{ $_locale('pieces', false) }} á
            {{ $_formatCurrency(key, true, 3) }}
            {{ $_CURRENCY_SYMBOL }}
            </span>
        </p>
        <template v-if="!adminMode">
        <p v-for="(obj, key) in membershipStampsQuantity"
            :key="key"
            :class="{ 'text-danger': !obj.valid }">
            {{ obj.quantity }}
            {{ $_locale('pieces', false) }}
            {{ obj.name }}
        </p>
        </template>
        <p style="font-weight: bold;">
            {{ $_locale('total') }}:
            {{ $_formatCurrency(totalPrice) }}
            {{ $_CURRENCY_SYMBOL }}
        </p>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { durationToBlockCount } from '../common/ReservationService.js'

export default {
    props: {
        additionalReservations: {
            type: Array,
            required: true,
            default: () => [],
        }
    },
    computed: {
        ...mapGetters({
            adminMode: 'common/adminMode',
            activeReservation: 'calendar/activeReservation',
            availableAdditionalResources: 'calendar/availableAdditionalResources',
            additionalResourceQuantity: 'calendar/additionalResourceQuantity',
            settings: 'reservations/settings',
            resourceRules: 'reservations/resourceRules',
            minutePrice: 'reservations/minutePrice',
            membershipStampsQuantity: 'memberships/membershipStampsQuantity',
        }),
        resourceQuantity() {
            return (resourceId) =>  {
                return this.additionalResourceQuantity[resourceId] || 0;
            }
        },
        selectedAdditionalResources() {
            return this.availableAdditionalResources
                .map((resource) => {
                    resource.orderQuantity = this.additionalResourceQuantity[resource.resourceId] || 0;
                    return resource;
                })
                .filter(
                    (resource) => resource.orderQuantity > 0 && !!resource.productPrice
                )
        },
        totalPrice() {
            let totalPrice = 0

            if (this.minutePrice !== null) {
                totalPrice = this.minutePrice
            } else {
                let stampQuantity = null;

                if (!this.adminMode) {
                    stampQuantity = Object.keys(this.membershipStampsQuantity)
                        .map(key => {
                            const stamp = this.membershipStampsQuantity[key];

                            return stamp.quantity;
                        })
                        .reduce((prev, curr) => prev + curr, 0);
                }
                // remember orderQuantity is how many people per block
                // quantity is how many blocks, the price calculates block prices
                const { orderQuantity, selectedIndex, getTotalPrice } = this.activeReservation;
                const price = getTotalPrice(selectedIndex, stampQuantity);
                totalPrice = price * orderQuantity;
            }

            if (this.additionalReservations.length > 0) {
                this.additionalReservations.forEach((resObj) => {
                    totalPrice += resObj.price;
                })
            }

            if (!this.activeReservation.recurringReservation
                    && this.availableAdditionalResources.length > 0) {
                const additionalResources = this.selectedAdditionalResources;

                additionalResources.forEach((resource) => {
                    let quantity = resource.orderQuantity;

                    if (resource.productType === 6) {
                        const start = this.activeReservation.start.clone();
                        // by default, use resource min duration (this was just implemented first)
                        let end = start.clone().add(resource.productReservationMinDuration, 'minutes');

                        quantity = resource.productReservationMinDuration / resource.productReservationDuration;
                        // also an option to use same duration as activeReservation
                        if (resource.option == 2) {
                            end = this.activeReservation.end.clone();
                            quantity = durationToBlockCount(start, end, resource.productReservationDuration);
                        }
                    }
                    totalPrice += -(-resource.productPrice) * quantity;
                })
            }

            return totalPrice * (this.activeReservation.recurringDates.length + 1);
        },
        additionalReservationsPriceClasses() {
            const priceClasses = {};

            if (this.additionalReservations.length === 0) {
                return priceClasses;
            }

            this.additionalReservations.forEach((resObj) => {
                const { availableBlocks, selectedIndex, aPrice, quantity, resource } = resObj;
                const selectedBlocks = availableBlocks.slice(0, selectedIndex + 1);
                const key = aPrice;

                if (typeof priceClasses[key] !== 'undefined') {
                    priceClasses[key].quantity += quantity;
                    priceClasses[key].blocks = {
                        ...priceClasses[key].blocks,
                        ...selectedBlocks,
                    };
                    priceClasses[key].names = Array.from(
                        new Set([
                            ...priceClasses[key].names,
                            resource.resourceName
                        ])
                    )
                } else {
                    priceClasses[key] = {
                        quantity,
                        blocks: selectedBlocks,
                        names: [ resource.resourceName ]
                    }
                }
            })

            return priceClasses;
        },
        additionalResourcesPriceClasses() {
            const additionalResources = this.selectedAdditionalResources;
            const priceClasses = {};

            if (this.activeReservation.recurringReservation
                    || this.availableAdditionalResources.length === 0
                    || additionalResources.length === 0) {
                return priceClasses;
            }

            additionalResources.forEach((resource) => {
                const key = -(-resource.productPrice);
                const blocks = Array(resource.orderQuantity).fill({
                    price: -(-resource.productPrice),
                    resource: resource
                });
                let quantity = resource.orderQuantity;

                if (resource.productType === 6) {
                    const start = this.activeReservation.start.clone();
                    // by default, use resource min duration (this was just implemented first)
                    let end = start.clone().add(resource.productReservationMinDuration, 'minutes');

                    quantity = resource.productReservationMinDuration / resource.productReservationDuration;
                    // also an option to use same duration as activeReservation
                    if (resource.option == 2) {
                        end = this.activeReservation.end.clone();
                        quantity = durationToBlockCount(start, end, resource.productReservationDuration);
                    }
                }

                const obj = {
                    quantity,
                    blocks,
                    names: [ resource.resourceName ]
                };

                if (typeof priceClasses[key] !== 'undefined') {
                    priceClasses[key].quantity += obj.quantity;
                    priceClasses[key].blocks = {
                        ...priceClasses[key].blocks,
                        ...obj.blocks,
                    };
                    priceClasses[key].names = Array.from(
                        new Set([
                            ...priceClasses[key].names,
                            resource.resourceName
                        ])
                    )
                } else {
                    priceClasses[key] = obj;
                }
            })

            return priceClasses;
        },
        priceClasses() {
            let priceClasses = JSON.parse(JSON.stringify(this.activeReservation.priceClasses)); // deep copy

            if (this.minutePrice !== null) {
                const [ firstKey ] = Object.keys(priceClasses)
                const example = priceClasses[firstKey]
                const [ firstBlock ] = example.blocks

                example.quantity = 1
                example.blocks = [ firstBlock ]
                priceClasses = {
                    [this.minutePrice]: { ...example }
                }
            }

            const additionalResources = this.additionalResourcesPriceClasses;
            const additionalReservations = this.additionalReservationsPriceClasses;

            Object.keys(additionalResources).forEach(key => {
                const obj = additionalResources[key];

                if (typeof priceClasses[key] !== 'undefined') {
                    priceClasses[key].quantity += obj.quantity;
                    priceClasses[key].blocks = {
                        ...priceClasses[key].blocks,
                        ...obj.blocks,
                    };
                    priceClasses[key].names = Array.from(
                        new Set([
                            ...priceClasses[key].names,
                            ...obj.names,
                        ])
                    );
                } else {
                    priceClasses[key] = { ...obj };
                }
            })

            Object.keys(additionalReservations).forEach(key => {
                const obj = additionalReservations[key];

                if (typeof priceClasses[key] !== 'undefined') {
                    priceClasses[key].quantity += obj.quantity;
                    priceClasses[key].blocks = {
                        ...priceClasses[key].blocks,
                        ...obj.blocks,
                    };
                    priceClasses[key].names = Array.from(
                        new Set([
                            ...priceClasses[key].names,
                            ...obj.names,
                        ])
                    );
                } else {
                    priceClasses[key] = { ...obj };
                }
            })

            return priceClasses;
        },
        /*
        priceClasses_() {
            /**
             * - this idea was that a reservation object would create rows of prices and quantities
             * based on the selected blocks
             * - additional reservations have similar data
             * - from that would the priceclasses object be built
            const priceRows = [
                this.activeReservation
            ]
        }
             */
    },
    methods: {
        getTotalPrice() {
            return this.totalPrice;
        }
    },
}
</script>

<style scoped>
.resource-row {
    display: flex;
    gap: 7px;
    justify-content: flex-end;
    flex-wrap: wrap;
}
</style>